import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  askAIChatCall,
  askTutorChatCall,
  getAIMessagesByThreadCall,
} from "../../redux/api/ai.api";
import { quizCopilotChatCall } from "../../redux/api/mcpQuiz.api";
import { getQuizResultsCall } from "../../redux/api/quizSystem.api";
import { GetUniversityStudentsCall } from "../../redux/api/student.api";
import { getDashboardRouteContext } from "./routeContext";
import {
  clearPersistedConversation,
  conversationReducer,
  createMessage,
  initialConversationState,
  loadPersistedConversation,
  mapThreadMessages,
  persistConversation,
  sanitizeTutorContent,
} from "./AIConversationState";
import {
  MESSAGE_STATUS,
  RESULTS_OPTION_CHIPS,
  USERS_OPTION_CHIPS,
  formatQuizResultsAnalysis,
  formatUsersDirectory,
  getLocalAssistantFallback,
  parseLocalQuizCreatePrompt,
} from "./constants";
import { useQuizDraftOptional } from "./quiz/QuizDraftContext";
import {
  isQuizCopilotIntent,
  isQuizResultsIntent,
  isQuizUsersIntent,
} from "./quiz/quizDraftDefaults";

const AIAssistantContext = createContext(null);
AIAssistantContext.displayName = "AIAssistantContext";

function resolveAuthUserId(auth) {
  if (!auth) return null;
  return auth.id || auth.user_id || auth.user?.id || auth.email || null;
}

function hasAccessToken(auth) {
  return Boolean(auth?.access_token);
}

function buildHistory(messages) {
  return (messages || [])
    .filter((m) => m.status !== MESSAGE_STATUS.FAILED)
    .slice(-12)
    .map((m) => ({
      role: m.role === "assistant" ? "assistant" : "user",
      content: String(m.content || "").slice(0, 2000),
    }));
}

export function AIAssistantProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const userId = resolveAuthUserId(auth);
  const isAuthed = hasAccessToken(auth);
  const quizDraftApi = useQuizDraftOptional();

  const [state, dispatch] = useReducer(conversationReducer, initialConversationState);
  const [copilotUi, setCopilotUi] = useState({
    confirmation: null,
    createdQuiz: null,
    optionChips: [],
    confirming: false,
  });
  const restoreAttemptedRef = useRef(null);
  const confirmRequestIdRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const routeContext = useMemo(
    () => getDashboardRouteContext(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!isAuthed || !userId) {
      if (state.ownerUserId) {
        dispatch({ type: "RESET_FOR_SECURITY" });
      }
      restoreAttemptedRef.current = null;
      return;
    }

    if (state.ownerUserId === String(userId)) return;

    const saved = loadPersistedConversation(userId);
    dispatch({ type: "BIND_OWNER", ownerUserId: String(userId) });
    if (saved) {
      dispatch({
        type: "HYDRATE",
        payload: { ...saved, ownerUserId: String(userId) },
      });
    }
  }, [isAuthed, userId, state.ownerUserId]);

  useEffect(() => {
    if (!state.ownerUserId) return;
    persistConversation(state.ownerUserId, {
      threadId: state.threadId,
      messages: state.messages,
      draft: state.draft,
    });
  }, [state.ownerUserId, state.threadId, state.messages, state.draft]);

  useEffect(() => {
    if (quizDraftApi?.draft && quizDraftApi.draft.awaiting_confirmation === false) {
      setCopilotUi((prev) =>
        prev.confirmation ? { ...prev, confirmation: null } : prev
      );
    }
  }, [quizDraftApi?.draft?.awaiting_confirmation]);

  useEffect(() => {
    if (!isAuthed || !userId || !state.threadId) return;
    if (state.messages.length > 0) return;
    const attemptKey = `${userId}:${state.threadId}`;
    if (restoreAttemptedRef.current === attemptKey) return;
    restoreAttemptedRef.current = attemptKey;

    let cancelled = false;
    (async () => {
      dispatch({ type: "SET_RESTORING", restoring: true });
      try {
        const res = await getAIMessagesByThreadCall(state.threadId);
        if (cancelled) return;
        const mapped = mapThreadMessages(res);
        if (mapped.length) {
          dispatch({ type: "SET_MESSAGES", messages: mapped });
        }
      } catch {
        if (!cancelled) {
          dispatch({
            type: "SET_ERROR",
            error: "Could not restore the previous conversation. You can continue or start a new one.",
          });
        }
      } finally {
        if (!cancelled) dispatch({ type: "SET_RESTORING", restoring: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthed, userId, state.threadId, state.messages.length]);

  const open = useCallback(() => dispatch({ type: "OPEN" }), []);
  const close = useCallback(() => dispatch({ type: "CLOSE" }), []);
  const toggle = useCallback(() => dispatch({ type: "TOGGLE" }), []);
  const setDraft = useCallback((draft) => dispatch({ type: "SET_DRAFT", draft }), []);
  const clearError = useCallback(() => dispatch({ type: "SET_ERROR", error: null }), []);

  const startNewConversation = useCallback(() => {
    const owner = stateRef.current.ownerUserId;
    dispatch({ type: "NEW_CONVERSATION" });
    if (owner) {
      persistConversation(owner, { threadId: null, messages: [], draft: "" });
    }
    restoreAttemptedRef.current = null;
    confirmRequestIdRef.current = null;
    setCopilotUi({
      confirmation: null,
      createdQuiz: null,
      optionChips: [],
      confirming: false,
    });
    quizDraftApi?.clearDraft?.();
  }, [quizDraftApi]);

  const applyCopilotResponse = useCallback(
    (res) => {
      if (res?.draft && quizDraftApi) {
        quizDraftApi.applyCopilotResult(res);
      }
      const options = Array.isArray(res?.ui_actions?.options) ? res.ui_actions.options : [];
      setCopilotUi((prev) => ({
        ...prev,
        confirmation: res?.ui_actions?.confirmation || (res?.needs_confirmation ? res?.ui_actions?.confirmation : null) || (res?.needs_confirmation ? previewFromDraft(res.draft) : null),
        createdQuiz: res?.quiz || res?.ui_actions?.created_quiz || prev.createdQuiz,
        optionChips: options,
        confirming: false,
      }));
      if (res?.ui_actions?.navigate_to && res?.quiz?.id) {
        navigate(res.ui_actions.navigate_to, { replace: false });
      }
    },
    [navigate, quizDraftApi]
  );

  const shouldUseQuizCopilot = useCallback(
    (text) => {
      if (quizDraftApi?.draft?.awaiting_confirmation) return true;
      if ((quizDraftApi?.draft?.topic_rules || []).length > 0) return true;
      if (copilotUi.confirmation) return true;
      if (routeContext.key === "create_quiz" && isQuizCopilotIntent(text)) return true;
      return isQuizCopilotIntent(text);
    },
    [quizDraftApi, copilotUi.confirmation, routeContext.key]
  );

  const applyAssistantText = useCallback((userMsgId, reply, options = []) => {
    dispatch({
      type: "UPDATE_MESSAGE",
      id: userMsgId,
      patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
    });
    dispatch({
      type: "APPEND_MESSAGE",
      message: createMessage({
        role: "assistant",
        content: reply,
        status: MESSAGE_STATUS.SENT,
      }),
    });
    setCopilotUi((prev) => ({
      ...prev,
      optionChips: (options || []).map((o) => ({
        id: o.id,
        label: o.label,
        prompt: o.prompt || o.label,
      })),
      confirmation: null,
      confirming: false,
    }));
  }, []);

  const loadResultsAnalysisLocally = useCallback(async () => {
    try {
      const res = await getQuizResultsCall({ page: 1, limit: 25 });
      if (res?.status === 401 || res?.status === 403) {
        return {
          reply:
            res?.message ||
            "You are not authorized to view quiz results for these classes.",
          options: RESULTS_OPTION_CHIPS,
        };
      }
      if (!res || (typeof res.status === "number" && res.status >= 400)) {
        return {
          reply:
            "I couldn’t load quiz results right now. Open the Results page or try again in a moment.",
          options: RESULTS_OPTION_CHIPS,
        };
      }
      return formatQuizResultsAnalysis(res);
    } catch {
      return {
        reply:
          "I couldn’t load quiz results right now. Open the Results page or try again in a moment.",
        options: RESULTS_OPTION_CHIPS,
      };
    }
  }, []);

  const loadUsersDirectoryLocally = useCallback(async () => {
    try {
      const role = String(auth?.role || auth?.user?.role || "")
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
      const universityId =
        auth?.university_id || auth?.user?.university_id || auth?.universityId || null;
      const isUniAdmin = role === "university_admin";

      if (isUniAdmin && universityId) {
        const students = await GetUniversityStudentsCall(universityId);
        return formatUsersDirectory(students, { universityWide: true });
      }

      // Professors: offline path stays class-scoped via unique students in quiz results.
      const res = await getQuizResultsCall({ page: 1, limit: 50 });
      const body = res?.data && typeof res.data === "object" ? res.data : res;
      const results = Array.isArray(body?.results) ? body.results : [];
      const byEmail = new Map();
      results.forEach((r) => {
        const key = r.student_email || r.user_id || r.student_name;
        if (!key || byEmail.has(key)) return;
        byEmail.set(key, {
          name: r.student_name || "Student",
          email: r.student_email || "—",
          role: "student",
        });
      });
      return formatUsersDirectory([...byEmail.values()], { universityWide: false });
    } catch {
      return {
        reply:
          "I couldn’t load users right now. Try again in a moment, or ask about marks once Quiz Copilot is back online.",
        options: USERS_OPTION_CHIPS,
      };
    }
  }, [auth]);

  const sendMessage = useCallback(
    async (rawText, { retryOfId, confirmCreate = false } = {}) => {
      const text = String(rawText || "").trim();
      if ((!text && !confirmCreate) || stateRef.current.sending) return;

      // Chip shortcut — don't add a chat turn.
      if (text === "__open_results__") {
        navigate("/quiz/results");
        return;
      }

      if (!isAuthed || !hasAccessToken(auth)) {
        dispatch({
          type: "SET_ERROR",
          error: "Please sign in again to use the AI assistant.",
        });
        return;
      }

      dispatch({ type: "SET_ERROR", error: null });

      if (retryOfId) {
        dispatch({ type: "REMOVE_MESSAGE", id: retryOfId });
      }

      const displayText = confirmCreate ? text || "Create Quiz" : text;
      const resultsIntent = isQuizResultsIntent(displayText);
      const usersIntent = isQuizUsersIntent(displayText);
      const userMsg = createMessage({
        role: "user",
        content: displayText,
        status: MESSAGE_STATUS.PENDING,
        retryPayload: { text: displayText },
      });
      dispatch({ type: "APPEND_MESSAGE", message: userMsg });
      dispatch({ type: "SET_DRAFT", draft: "" });
      dispatch({ type: "SET_SENDING", sending: true });

      try {
        // Local starter / coming-soon / offline create replies.
        // Results/users/marks always go through live data (never create-quiz stubs).
        const local = getLocalAssistantFallback(displayText);
        const parsedCreate = parseLocalQuizCreatePrompt(displayText);
        const useLocalOnly =
          !!local &&
          !confirmCreate &&
          !resultsIntent &&
          !usersIntent &&
          (/presentation/i.test(displayText) ||
            /^create a quiz\.?$/i.test(displayText.trim()) ||
            /^cat:/i.test(displayText.trim()) ||
            !!parsedCreate ||
            /private question bank/i.test(displayText) ||
            /help me with my quizzes|ask about my quizzes/i.test(displayText));

        if (useLocalOnly) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
          });
          dispatch({
            type: "APPEND_MESSAGE",
            message: createMessage({
              role: "assistant",
              content: local.reply,
              status: MESSAGE_STATUS.SENT,
            }),
          });
          if (local.draft && quizDraftApi?.setDraft) {
            quizDraftApi.setDraft(local.draft);
          }
          setCopilotUi((prev) => ({
            ...prev,
            optionChips: (local.options || []).map((o) => ({
              id: o.id,
              label: o.label,
              prompt: o.prompt || o.label,
            })),
            confirmation: local.confirmation || null,
            confirming: false,
          }));
          return;
        }

        if (
          shouldUseQuizCopilot(displayText) ||
          confirmCreate ||
          resultsIntent ||
          usersIntent
        ) {
          if (confirmCreate && !confirmRequestIdRef.current) {
            confirmRequestIdRef.current = `quiz-create-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 8)}`;
          }
          setCopilotUi((prev) => ({ ...prev, confirming: !!confirmCreate }));

          const res = await quizCopilotChatCall({
            message: displayText,
            draft: quizDraftApi?.draft || null,
            confirm_create: !!confirmCreate,
            client_request_id: confirmCreate ? confirmRequestIdRef.current : null,
            dashboard: routeContext,
            history: buildHistory(stateRef.current.messages),
          });

          if (res?.status === 401 || res?.status === 403) {
            dispatch({
              type: "UPDATE_MESSAGE",
              id: userMsg.id,
              patch: { status: MESSAGE_STATUS.FAILED },
            });
            dispatch({
              type: "SET_ERROR",
              error:
                res?.message ||
                "Your session is no longer valid or you are not authorized for Quiz Copilot.",
            });
            return;
          }

          if (!res || (typeof res.status === "number" && res.status >= 400 && !res.reply)) {
            // Confirm while offline → open Create Quiz with the local draft filled in.
            if (confirmCreate && quizDraftApi?.draft) {
              quizDraftApi.setDraft({
                ...quizDraftApi.draft,
                awaiting_confirmation: false,
              });
              quizDraftApi.applyCopilotResult?.({
                draft: quizDraftApi.draft,
                ui_actions: { open_quiz_form: true },
              });
              applyAssistantText(
                userMsg.id,
                "Quiz Copilot is offline, so I opened the Create Quiz form with your draft. Review and save it there."
              );
              navigate("/quiz/create");
              return;
            }

            // Results offline → hit the results API directly and analyze.
            if (resultsIntent) {
              const analysis = await loadResultsAnalysisLocally();
              applyAssistantText(userMsg.id, analysis.reply, analysis.options);
              return;
            }

            // Users/directory offline → university students (admin) or class-scoped results.
            if (usersIntent) {
              const directory = await loadUsersDirectoryLocally();
              applyAssistantText(userMsg.id, directory.reply, directory.options);
              return;
            }

            // API missing/offline — keep the chat useful with a local fallback reply.
            const fallback =
              getLocalAssistantFallback(displayText) || {
                reply:
                  "I couldn’t reach the Quiz Copilot service just now. You can still pick a topic below, or try again in a moment.",
                options: [
                  { id: "cat:histology", label: "Histology (Microlab)", prompt: "cat:histology" },
                  { id: "cat:embryology", label: "Embryology (Microlab)", prompt: "cat:embryology" },
                  { id: "cat:anatomy", label: "Anatomy (Microlab)", prompt: "cat:anatomy" },
                  {
                    id: "bank:private",
                    label: "My private question bank",
                    prompt: "Create a quiz using my private question bank.",
                  },
                ],
              };
            if (fallback.draft && quizDraftApi?.setDraft) {
              quizDraftApi.setDraft(fallback.draft);
            }
            dispatch({
              type: "UPDATE_MESSAGE",
              id: userMsg.id,
              patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
            });
            dispatch({
              type: "APPEND_MESSAGE",
              message: createMessage({
                role: "assistant",
                content: fallback.reply,
                status: MESSAGE_STATUS.SENT,
              }),
            });
            setCopilotUi((prev) => ({
              ...prev,
              optionChips: (fallback.options || []).map((o) => ({
                id: o.id,
                label: o.label,
                prompt: o.prompt || o.label,
              })),
              confirmation: fallback.confirmation || null,
              confirming: false,
            }));
            return;
          }

          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
          });

          if (res.reply) {
            dispatch({
              type: "APPEND_MESSAGE",
              message: createMessage({
                role: "assistant",
                content: res.reply,
                status: MESSAGE_STATUS.SENT,
              }),
            });
          }

          applyCopilotResponse(res);

          // Prefer Results chips for analysis turns.
          if (resultsIntent) {
            const opts = Array.isArray(res?.ui_actions?.options) ? res.ui_actions.options : [];
            setCopilotUi((prev) => ({
              ...prev,
              optionChips: (opts.length ? opts : RESULTS_OPTION_CHIPS).map((o) => ({
                id: o.id,
                label: o.label,
                prompt: o.prompt || o.label,
              })),
            }));
          }

          if (res.quiz?.id) {
            confirmRequestIdRef.current = null;
          }
          return;
        }

        // Generic local fallback for starter asks that didn't route to quiz-copilot
        if (local) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
          });
          dispatch({
            type: "APPEND_MESSAGE",
            message: createMessage({
              role: "assistant",
              content: local.reply,
              status: MESSAGE_STATUS.SENT,
            }),
          });
          setCopilotUi((prev) => ({
            ...prev,
            optionChips: local.options || [],
          }));
          return;
        }

        // Generic tutor chat (Phase 1 path)
        const locale = (localStorage.getItem("i18nextLng") || "en").split("-")[0] || "en";
        const threadId = stateRef.current.threadId;
        const formData = new FormData();
        formData.append("body", text);
        formData.append("type", "ai");
        formData.append("entity_type", "aiQuestion");
        formData.append("language", locale);
        formData.append("dashboard_section", routeContext.key);
        formData.append("dashboard_section_label", routeContext.label);
        if (threadId) {
          formData.append("thread_id", threadId);
          formData.append("title", "histology");
        }

        const res = threadId ? await askAIChatCall(formData) : await askTutorChatCall(formData);

        if (res?.status === 401 || res?.status === 403) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.FAILED },
          });
          dispatch({
            type: "SET_ERROR",
            error: "Your session is no longer valid. Please sign in again.",
          });
          return;
        }

        if (res?.status === 402 || res?.code === "INSUFFICIENT_CREDITS") {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.FAILED },
          });
          dispatch({
            type: "SET_ERROR",
            error:
              res?.message ||
              "Insufficient AI credits to start a new conversation. Existing chats remain available.",
          });
          return;
        }

        const failed =
          !res ||
          (typeof res.status === "number" && res.status >= 400) ||
          (!threadId && !res.thread_id && !res?.messages && !res?.tutor);

        if (failed) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.FAILED },
          });
          dispatch({
            type: "SET_ERROR",
            error: res?.message || "The message could not be sent. Check your connection and retry.",
          });
          return;
        }

        dispatch({
          type: "UPDATE_MESSAGE",
          id: userMsg.id,
          patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
        });

        const nextThreadId = res.thread_id || threadId;
        if (nextThreadId && nextThreadId !== threadId) {
          dispatch({ type: "SET_THREAD", threadId: nextThreadId });
        }

        const aiText = sanitizeTutorContent(
          res?.tutor?.answer ||
            res?.messages?.[1]?.content ||
            res?.messages?.[res?.messages?.length - 1]?.content ||
            ""
        );

        if (aiText) {
          dispatch({
            type: "APPEND_MESSAGE",
            message: createMessage({
              role: "assistant",
              content: aiText,
              status: MESSAGE_STATUS.SENT,
            }),
          });
        }
      } catch (err) {
        dispatch({
          type: "UPDATE_MESSAGE",
          id: userMsg.id,
          patch: { status: MESSAGE_STATUS.FAILED },
        });
        dispatch({
          type: "SET_ERROR",
          error:
            err?.message?.includes("Network") || !navigator.onLine
              ? "You appear to be offline. Reconnect and retry your message."
              : "Network error while contacting the AI assistant. Please retry.",
        });
      } finally {
        dispatch({ type: "SET_SENDING", sending: false });
        setCopilotUi((prev) => ({ ...prev, confirming: false }));
      }
    },
    [
      auth,
      isAuthed,
      routeContext,
      shouldUseQuizCopilot,
      quizDraftApi,
      applyCopilotResponse,
      applyAssistantText,
      loadResultsAnalysisLocally,
      navigate,
    ]
  );

  const confirmCreateQuiz = useCallback(() => {
    sendMessage("Create Quiz", { confirmCreate: true });
  }, [sendMessage]);

  const editQuizConfiguration = useCallback(() => {
    setCopilotUi((prev) => ({ ...prev, confirmation: null }));
    quizDraftApi?.setDraft?.((d) => ({
      ...d,
      awaiting_confirmation: false,
    }));
    sendMessage("I want to edit the quiz configuration.");
  }, [quizDraftApi, sendMessage]);

  const openCreatedQuiz = useCallback(() => {
    const quiz = copilotUi.createdQuiz;
    if (!quiz?.id) return;
    navigate(`/quiz/create?highlight=${encodeURIComponent(quiz.id)}`);
  }, [copilotUi.createdQuiz, navigate]);

  const openCreatedQuizResults = useCallback(() => {
    navigate("/quiz/results");
  }, [navigate]);

  const retryMessage = useCallback(
    (message) => {
      if (!message?.retryPayload?.text && message?.role !== "user") return;
      const text = message.retryPayload?.text || message.content;
      sendMessage(text, { retryOfId: message.id });
    },
    [sendMessage]
  );

  const value = useMemo(
    () => ({
      ...state,
      routeContext,
      open,
      close,
      toggle,
      setDraft,
      clearError,
      sendMessage,
      retryMessage,
      startNewConversation,
      copilotUi,
      confirmCreateQuiz,
      editQuizConfiguration,
      openCreatedQuiz,
      openCreatedQuizResults,
      clearLocalPersistence: () => {
        if (state.ownerUserId) clearPersistedConversation(state.ownerUserId);
      },
    }),
    [
      state,
      routeContext,
      open,
      close,
      toggle,
      setDraft,
      clearError,
      sendMessage,
      retryMessage,
      startNewConversation,
      copilotUi,
      confirmCreateQuiz,
      editQuizConfiguration,
      openCreatedQuiz,
      openCreatedQuizResults,
    ]
  );

  return (
    <AIAssistantContext.Provider value={value}>{children}</AIAssistantContext.Provider>
  );
}

function previewFromDraft(draft) {
  if (!draft) return null;
  const total = (draft.topic_rules || []).reduce(
    (sum, r) => sum + (Number(r.num_questions) || 0),
    0
  );
  return {
    title: draft.title || "(untitled)",
    time_limit_minutes: draft.time_limit_minutes || 30,
    total_questions: total,
    topic_rules: (draft.topic_rules || []).map((r) => ({
      source: r.bank_source,
      catalogue: r.catalogue || null,
      topic_label: r.topic_label || null,
      subtitles:
        Array.isArray(r.subtitle_labels) && r.subtitle_labels.length
          ? r.subtitle_labels
          : Array.isArray(r.quiz_title_ids) && r.quiz_title_ids.length
            ? [`${r.quiz_title_ids.length} selected`]
            : ["All"],
      difficulty: r.difficulty_filter || "any",
      num_questions: r.num_questions,
    })),
    instructions_preview: String(draft.rules_html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 240),
  };
}

AIAssistantProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAIAssistant() {
  const ctx = useContext(AIAssistantContext);
  if (!ctx) {
    throw new Error("useAIAssistant must be used within AIAssistantProvider");
  }
  return ctx;
}

export function useAIAssistantOptional() {
  return useContext(AIAssistantContext);
}

export default AIAssistantProvider;
